import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import type { BusinessLabelInfo } from '@/api/generated/models/BusinessLabelInfo'

interface BusinessLabelSelectorProps {
  labels: BusinessLabelInfo[]
  selectedLabels: string[]
  onLabelsChange: (selectedLabels: string[]) => void
  isLoading?: boolean
  error?: string | null
}

const BusinessLabelSelector: React.FC<BusinessLabelSelectorProps> = ({
  labels,
  selectedLabels,
  onLabelsChange,
  isLoading = false,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleLabelToggle = (labelId: string) => {
    const isSelected = selectedLabels.includes(labelId)

    if (isSelected) {
      onLabelsChange(selectedLabels.filter(id => id !== labelId))
    } else {
      onLabelsChange([...selectedLabels, labelId])
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Get selected label names for display
  const getSelectedLabelNames = () => {
    if (selectedLabels.length === 0) return ''
    return labels
      .filter(label => selectedLabels.includes(label.id))
      .map(label => label.key)
      .join(', ')
  }

  if (isLoading) {
    return (
      <div style={{
        padding: '16px',
        textAlign: 'center',
        color: designTokens.colors.gray[4]
      }}>
        라벨 정보를 불러오는 중...
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        padding: '16px',
        textAlign: 'center',
        color: designTokens.colors.error.DEFAULT
      }}>
        {error}
      </div>
    )
  }

  if (!labels || labels.length === 0) {
    return (
      <div style={{
        padding: '16px',
        textAlign: 'center',
        color: designTokens.colors.gray[4]
      }}>
        사용 가능한 라벨이 없습니다.
      </div>
    )
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Dropdown Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '10px 12px',
          border: `1px solid ${designTokens.colors.gray[2]}`,
          borderRadius: '6px',
          backgroundColor: designTokens.colors.white,
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          color: selectedLabels.length > 0 ? designTokens.colors.gray[6] : designTokens.colors.gray[4],
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '40px'
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {selectedLabels.length > 0 ? getSelectedLabelNames() : '라벨을 선택해주세요'}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: designTokens.colors.gray[4] }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            marginTop: '4px',
            backgroundColor: designTokens.colors.white,
            border: `1px solid ${designTokens.colors.gray[2]}`,
            borderRadius: '6px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            maxHeight: '200px',
            overflowY: 'auto'
          }}
        >
          {labels.map(label => {
            const isSelected = selectedLabels.includes(label.id)

            return (
              <label
                key={label.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  backgroundColor: isSelected ? designTokens.colors.primary.light : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = designTokens.colors.gray[0]
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleLabelToggle(label.id)}
                  style={{
                    width: '16px',
                    height: '16px',
                    accentColor: designTokens.colors.primary.DEFAULT
                  }}
                />
                <span style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-medium'][0],
                  fontWeight: 400,
                  color: isSelected ? designTokens.colors.primary.dark : designTokens.colors.gray[6]
                }}>
                  {label.key}
                </span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default BusinessLabelSelector